import {getRepositoryContextCrumb} from './context-crumbs'
import {getDisplayedContext, navigateWithContextRefresh} from './navigate-with-context-refresh'

// Account issues (`/orgs/:org/issues/:number` and `/users/:user/issues/:number`) share this shape but aren't
// repositories, so the account prefixes are excluded.
const ISSUE_OR_PULL_REQUEST_PATH = /^\/(?!(?:orgs|users)\/)([^/]+)\/([^/]+)\/(pull|issues)\/\d+(?:\/|$)/
const SELECTED_LINK_BY_SEGMENT = {pull: 'repo_pulls', issues: 'repo_issues'} as const

/** The parts of a native or React click event used to decide whether to handle it. */
export type CrossRepositoryLinkClickEvent = Pick<
  MouseEvent,
  'button' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'defaultPrevented' | 'preventDefault' | 'target'
>

export interface CrossRepositoryNavigationOptions {
  /** The `owner/name` of the repository the page currently shows. Links within it are left alone. */
  currentRepositoryNameWithOwner: string
  /** Additional router navigation state. */
  state?: Record<string, unknown>
}

/**
 * Navigates the mounted React app to an issue or pull request in another repository and refreshes the global
 * navigation to it, selecting the Issues or Pull requests tab.
 *
 * Returns `false` without navigating when `destination` isn't an issue or pull request on this origin, is in the
 * current repository, or `navigateWithContextRefresh` declines (for example, while `global_nav_context_refresh` is
 * disabled), so the caller can keep its existing navigation.
 */
export function navigateToCrossRepositoryIssueOrPullRequest(
  destination: string,
  {currentRepositoryNameWithOwner, state}: CrossRepositoryNavigationOptions,
): boolean {
  const target = getIssueOrPullRequestDestination(destination)
  if (!target) return false
  if (target.repositoryNameWithOwner.toLowerCase() === currentRepositoryNameWithOwner.toLowerCase()) return false

  const sourceContext = getDisplayedContext(getRepositoryContextCrumb(currentRepositoryNameWithOwner), {})
  if (!sourceContext) return false

  return navigateWithContextRefresh({
    sourceContext,
    destinationContext: {
      ...getRepositoryContextCrumb(target.repositoryNameWithOwner),
      path: target.path,
      selectedLink: target.selectedLink,
    },
    state,
  })
}

/**
 * Handles a plain click on a link to an issue or pull request in another repository with
 * `navigateToCrossRepositoryIssueOrPullRequest`, and prevents the link's default only when that navigation starts.
 * Modified and middle clicks, new-tab and download links, links that opt out of Turbo, and clicks another handler
 * already handled keep their existing behavior. Pass `anchor` when it isn't the closest link to the event target.
 */
export function handleCrossRepositoryLinkClick(
  event: CrossRepositoryLinkClickEvent,
  {anchor, ...options}: CrossRepositoryNavigationOptions & {anchor?: HTMLAnchorElement | null},
): boolean {
  const targetAnchor = anchor === undefined ? getClosestAnchor(event.target) : anchor
  if (!targetAnchor || !isPlainClick(event, targetAnchor)) return false

  const navigated = navigateToCrossRepositoryIssueOrPullRequest(targetAnchor.href, options)
  if (navigated) event.preventDefault()
  return navigated
}

function getIssueOrPullRequestDestination(href: string) {
  let url: URL
  try {
    url = new URL(href, window.location.href)
  } catch {
    return undefined
  }
  if (url.origin !== window.location.origin) return undefined

  const match = ISSUE_OR_PULL_REQUEST_PATH.exec(url.pathname)
  if (!match) return undefined

  const [, owner, repo, segment] = match as unknown as [string, string, string, keyof typeof SELECTED_LINK_BY_SEGMENT]
  return {
    path: `${url.pathname}${url.search}${url.hash}`,
    repositoryNameWithOwner: `${owner}/${repo}`,
    selectedLink: SELECTED_LINK_BY_SEGMENT[segment],
  }
}

function getClosestAnchor(target: EventTarget | null): HTMLAnchorElement | null {
  return target instanceof Element ? target.closest('a') : null
}

function isPlainClick(event: CrossRepositoryLinkClickEvent, anchor: HTMLAnchorElement): boolean {
  const target = anchor.getAttribute('target')
  // Modified clicks open the link elsewhere, such as in a new tab, so they keep the link's default behavior.
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !isModifiedClick &&
    (!target || target === '_self') &&
    !anchor.hasAttribute('download') &&
    anchor.closest('[data-turbo]')?.getAttribute('data-turbo') !== 'false'
  )
}
